<script setup lang="ts">

import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { ModalDefault } from '@shared/ui/modals'
import { AppIcon } from '@shared/ui/icons'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'
import { AppBadge } from '@shared/ui/badges'

import LessonReassignReason from '../LessonReassignReason.vue'
import LessonTutorSelect from '../LessonTutorSelect.vue'
import LessonTutorFilterFields from '../LessonTutorFilterFields.vue'
import { useLessonEditTutorModal } from '../../../model/lessons/lesson-edit-tutor-modal.hook'

defineOptions({
  name: 'ModalLessonEditTutor',
})

const {
  refFilters,
  refTutorId,
  refTutorOptions,
  refIsTutorsLoading,
  refDate,
  refSelectedLabel,
  refKeptTutorName,
  refIsOffSchedule,
  refIsTutorReplaced,
  refReassignOptions,
  refReassignReason,
  refReassignComment,
  refIsReassignInvalid,
  refSaveText,
  refIsSaveDisabled,
  refIsSaving,
  setReassignReason,
  setReassignComment,
  applyFilters,
  clearFilters,
  save,
  close,
} = useLessonEditTutorModal()
</script>

<template>
  <ModalDefault
    show
    class-name="modal-lesson-dialog"
    title="Select tutor"
    :loading="refIsTutorsLoading || refIsSaving"
    @click:overlay="close"
  >
    <div class="modal-lesson-edit-tutor">
      <LessonTutorFilterFields
        :initial="refFilters"
        :disabled="refIsSaving || refIsTutorsLoading"
        @apply="applyFilters"
        @clear="clearFilters"
      />
      <div class="modal-lesson-edit-tutor__pick">
        <LessonTutorSelect
          v-model="refTutorId"
          :options="refTutorOptions"
          :date="refDate"
          :loading="refIsTutorsLoading"
          :disabled="refIsSaving"
        />
        <AppBadge
          v-if="refSelectedLabel"
          class="modal-lesson-edit-tutor__availability"
          :color="refSelectedLabel.color"
          :text="refSelectedLabel.text"
        />
        <AppText
          v-if="refKeptTutorName"
          name="body2"
          color="slate-500"
        >
          {{ refKeptTutorName }} is not among the tutors matching the filters and stays on the lesson unless you pick
          another one.
        </AppText>
      </div>
      <div
        v-if="refIsOffSchedule"
        class="modal-lesson-edit-tutor__warning"
        role="status"
      >
        <AppIcon
          :icon="faTriangleExclamation"
          color="amber-500"
        />
        <div class="modal-lesson-edit-tutor__warning-content">
          <AppText
            name="body2-medium"
            color="slate-900"
          >
            This time slot is outside the tutor's work schedule.
          </AppText>
          <AppText
            name="body2"
            color="slate-600"
          >
            The lesson will be saved anyway.
          </AppText>
        </div>
      </div>
      <div
        v-if="refIsTutorReplaced"
        class="modal-lesson-edit-tutor__reason"
      >
        <AppText
          name="body1-medium"
          color="slate-900"
        >
          Tutor change
        </AppText>
        <LessonReassignReason
          :options="refReassignOptions"
          :reason="refReassignReason"
          :comment="refReassignComment"
          :invalid="refIsReassignInvalid"
          :disabled="refIsSaving"
          @change:reason="setReassignReason"
          @change:comment="setReassignComment"
        />
      </div>
    </div>
    <template #controls>
      <div class="modal-lesson-form__controls">
        <BtnPrimary
          :text="refSaveText"
          color="emerald"
          :disabled="refIsSaveDisabled"
          @click="save"
        />
        <BtnSecondary
          text="Cancel"
          color="black"
          :disabled="refIsSaving"
          @click="close"
        />
      </div>
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalLessonForm.scss" />
